import type { NullishType } from "@qendo/util";
import { pluralize, singularize } from "inflector-js";
import { empty } from "~/util/index";

export function randomString(length = 16): string {
    return [...Array.from({ length }).keys()]
        .map(() => Math.random().toString(36))
        .join("")
        .substring(0, Math.max(1, length));
}

export function searchMatches(search: string, index: string[]): boolean {
    const terms = search
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/, " ")
        .split(/\s+/)
        .filter((term) => term.length > 0);

    for (const term of terms) {
        for (const compare of index) {
            if (compare.includes(term)) {
                return true;
            }
        }
    }

    return false;
}

export function begin(value: string, cap?: string | null): string {
    return empty(cap) || value.startsWith(cap) ? value : `${cap}${value}`;
}

export interface DigitOptions {
    minimumIntegerDigits?: number | undefined;
    maximumIntegerDigits?: number | undefined;
    minimumFractionDigits?: number | undefined;
    maximumFractionDigits?: number | undefined;
    minimumSignificantDigits?: number | undefined;
    maximumSignificantDigits?: number | undefined;
}

export type NumberFormatOptions = {
    style?: "decimal" | "currency" | "percent" | "unit";
    useGrouping?: "always" | "auto" | "min2" | boolean;
} & DigitOptions;

export function plural(
    input: string,
    count?: number | string | unknown[] | null,
    plural?: string | null,
    singular?: string | null,
): string {
    const length =
        Array.isArray(count) || (typeof count === "string" && Number.isNaN(count))
            ? count.length
            : Number.isNaN(count)
              ? count
                  ? 1
                  : 0
              : parseFloat(`${count}`);

    return length === 1 ? singularize(input, singular) : pluralize(input, plural);
}

export const DEFAULT_NUMBER_FORMAT: NumberFormatOptions = {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    minimumIntegerDigits: 1,
    useGrouping: false,
};
export const INTEGER_NUMBER_FORMAT: NumberFormatOptions = {
    maximumFractionDigits: 0,
};

const EMPTY_STRING = "";

export type NumericString = `${number}`;
export type EmptyString = typeof EMPTY_STRING;
export type Numeric = number | NumericString;
export type NumericOrEmptyString = NumericString | EmptyString;
export type NullableNumeric = Numeric | null;
export type OptionalNumeric = Numeric | NullishType;

export function formatNumeric(
    input: OptionalNumeric | EmptyString,
    formatterOptions?: NumberFormatOptions | undefined | null,
    min?: OptionalNumeric,
    max?: OptionalNumeric,
): NumericString | null {
    const value = `${input ?? EMPTY_STRING}`;

    if (!value.trim()) {
        return null;
    }

    const parsed = parseNumber(value, Number.NaN);

    if (Number.isNaN(parsed)) {
        return null;
    }

    const clamp_min = parseFloat(`${min ?? parsed}`);
    const clamp_max = parseFloat(`${max ?? parsed}`);

    const clamped = Math.max(clamp_min, Math.min(clamp_max, parsed));

    const formatter = new Intl.NumberFormat("en-AU", {
        ...DEFAULT_NUMBER_FORMAT,
        ...formatterOptions,
    });

    return formatter.format(clamped) as NumericString;
}

export function parseNumber(input: OptionalNumeric | string, on_nan: number = 0): number {
    const value = `${input ?? on_nan}`;

    const decimal = value.includes(".");
    const parsed = decimal
        ? parseFloat(value === "." ? "0" : value.replace(",", ""))
        : parseInt(value.replace(",", ""), 10);

    return Number.isNaN(parsed) ? on_nan : parsed;
}

export function formatFilesize(size: number): string {
    const units = ["B", "KB", "MB"];
    const limit = 1042;
    let factor = 1;
    const bytes = Math.abs(size);

    for (const unit of units) {
        const test = factor * limit;

        if (bytes <= test) {
            return `${formatNumeric(size / factor, { maximumFractionDigits: 1 })} ${unit}`;
        }

        factor = test;
    }

    return `${formatNumeric(size / factor, { maximumFractionDigits: 1 })} GB`;
}
